import { useCallback, useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useGoDesk } from "@/lib/floorReturn";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { ScanField } from "@/components/ScanField";
import { scanAccept, scanRefuse } from "@/lib/scanFeedback";
import { toast } from "sonner";
import { Truck, Undo2, Loader2, AlertTriangle, FileCheck2, Eye, Lock } from "lucide-react";
import { SourceTruckComponentPick } from "@/components/SourceTruckComponentPick";

// RECORD SOURCE TRUCK — the one gesture that marries a legacy part to the truck it came off, and
// the only way back off it. His rulings, and why each one is shaped the way it is:
//
//   · ONLY none -> truck. A part that has a truck is refused and told to REVERSE first. Reaching
//     truck B from truck A is impossible in one step AND in two, because the reversal refuses the
//     moment anything financial has referenced the marriage.
//   · NO CLOCK. "A clock doesn't protect money, it only decides how fast somebody has to notice."
//     What replaces it: both halves logged, and a part reversed twice shows up on /health.
//   · ALREADY SOLD? The record still gets the truth, and the dialog says out loud that the money
//     stays booked in the legacy bucket — because "the record and the money are different facts".
//   · HOW YOU KNOW is a tap-list he owns, each word marked EVIDENCE or JUDGEMENT, and the one that
//     means somebody told you REFUSES without their name.
const LEVEL = {
  critical: "border-[#B01E28] bg-red-50 text-[#B01E28]",
  high: "border-amber-400 bg-amber-50 text-amber-900",
  normal: "border-slate-300 bg-white text-slate-700",
};

export function SourceTruckAction({ part, onDone, big = false, testid = "source-truck" }) {
  const { has } = useAuth();
  const [open, setOpen] = useState(false);
  const [reasons, setReasons] = useState([]);
  const [reason, setReason] = useState(null);
  const [note, setNote] = useState("");
  const [toldBy, setToldBy] = useState("");
  const [target, setTarget] = useState("");
  const [component, setComponent] = useState("");
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);

  const id = part?.id;
  const assigned = !!part?.donor_id;

  useEffect(() => {
    if (!open || reasons.length) return;
    api.get("/source-reasons").then(({ data }) => setReasons(data.items || [])).catch(() => {});
  }, [open, reasons.length]);

  if (!has("assign_source_truck") || !id || assigned) return null;

  const reset = () => { setReason(null); setNote(""); setToldBy(""); setTarget(""); setComponent(""); setPreview(null); setBusy(false); };
  const shut = (refresh) => { setOpen(false); reset(); if (refresh) onDone?.(); };

  const body = (confirm) => ({
    to_scan: target.trim(), reason_code: reason?.code || "",
    note, told_by: toldBy, confirm, component: component || null,
  });

  const ask = async () => {
    setBusy(true);
    try { setPreview((await api.post(`/parts/${id}/source-truck`, body(false))).data); }
    catch (err) { scanRefuse(formatApiErrorDetail(err.response?.data?.detail)); toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const commit = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/parts/${id}/source-truck`, body(true));
      scanAccept(data.message); toast.success(data.message, { duration: 9000 });
      shut(true);
    } catch (err) { scanRefuse(formatApiErrorDetail(err.response?.data?.detail)); toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const needName = !!reason?.needs_name;
  const blocked = busy || !reason || !target.trim() || (reason?.reserved && !note.trim())
    || (needName && !toldBy.trim());

  return (
    <>
      <Button data-testid={`${testid}-open`} variant="outline"
        onClick={() => { reset(); setOpen(true); }}
        className={big
          ? "w-full h-12 rounded-xl border-2 border-slate-200 bg-white font-bold text-slate-700 justify-center"
          : "rounded-lg border-[#E0781A] text-[#C76714] hover:bg-orange-50 h-9"}>
        <Truck className={big ? "w-5 h-5 mr-2 text-[#E0781A]" : "w-4 h-4 mr-1.5"} />Record source truck
      </Button>

      <Dialog open={open} onOpenChange={(v) => { if (!v) shut(false); }}>
        <DialogContent className="rounded-xl max-w-lg max-h-kb overflow-y-auto">
          <DialogHeader>
            <DialogTitle data-testid={`${testid}-title`}>Which truck did this come off?</DialogTitle>
            <DialogDescription>
              This can be set once, from nothing. It can never be changed from one truck to another
              — if it turns out wrong, it gets taken back off to none and assigned again, and both
              halves stay in the history.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-3">
            <div className="rounded-lg border-2 border-[#E0781A]/40 bg-orange-50 p-2.5">
              <p className="text-xs font-bold text-[#C76714] mb-1.5">Scan the truck it came off</p>
              <ScanField testid={`${testid}-scan`} onScan={(v) => { setTarget(v); setPreview(null); }}
                submitLabel="Use this truck" placeholder="Scan the truck label or type its stock number…"
                uppercase autoFocus={false} />
              {target && <p data-testid={`${testid}-target`} className="text-xs text-slate-700 mt-1.5">
                Truck <b className="font-mono">{target}</b></p>}
            </div>

            <SourceTruckComponentPick partId={id} component={component} testid={testid}
              onComponent={(c) => { setComponent(c); setPreview(null); }}
              onMatch={(m) => { setTarget(m.stock_number || ""); setComponent(m.taken_by ? "" : m.component); setPreview(null); }} />

            <div>
              <p className="text-xs font-bold text-slate-600 mb-1.5">How do you know?</p>
              <div className="grid grid-cols-1 gap-1.5">
                {reasons.map((r) => (
                  <button key={r.code} type="button"
                    data-testid={`${testid}-reason-${r.code.replace(/[^a-z]+/g, "-")}`}
                    onClick={() => { setReason(r); setPreview(null); }}
                    className={`text-left rounded-lg border-2 px-3 py-2.5 text-sm font-semibold transition-all ${
                      reason?.code === r.code ? "border-[#E0781A] bg-orange-50 text-[#C76714]" : "border-slate-200 bg-white text-slate-700 hover:border-slate-400"}`}>
                    <span className="flex items-center gap-1.5">
                      {r.kind === "documented"
                        ? <FileCheck2 className="w-3.5 h-3.5 text-emerald-600 shrink-0" />
                        : <Eye className="w-3.5 h-3.5 text-slate-400 shrink-0" />}
                      {r.label}
                    </span>
                    <span className="block text-[11px] font-normal text-slate-500">
                      {r.kind === "documented" ? "documented evidence" : "somebody's judgement"}
                      {r.needs_name ? " · needs their name" : ""}
                      {r.reserved ? " · needs a written note" : ""}
                    </span>
                  </button>
                ))}
              </div>
            </div>

            {needName && (
              <Input data-testid={`${testid}-told-by`} value={toldBy}
                onChange={(e) => { setToldBy(e.target.value); setPreview(null); }}
                placeholder="Who told you? (a report with nobody's name on it isn't evidence)"
                className="rounded-lg h-10 text-sm" />
            )}

            <Textarea data-testid={`${testid}-note`} value={note} rows={2}
              onChange={(e) => { setNote(e.target.value); setPreview(null); }}
              placeholder={reason?.reserved ? "Say how you know (required for Other)" : "Anything worth adding (optional)"}
              className="rounded-lg text-sm" />

            {preview && (
              <div data-testid={`${testid}-confirm`} className="rounded-lg border-2 border-slate-300 bg-slate-50 p-3 space-y-2">
                <p className="text-sm font-semibold text-slate-800">{preview.prompt}</p>
                {(preview.warnings || []).map((w, i) => (
                  <p key={i} data-testid={`${testid}-warning-${i}`}
                    className={`text-xs font-semibold rounded-md border px-2 py-1.5 flex items-start gap-1.5 ${LEVEL[w.level] || LEVEL.normal}`}>
                    <AlertTriangle className="w-3.5 h-3.5 shrink-0 mt-0.5" />{w.text}
                  </p>
                ))}
                <p className="text-[11px] text-slate-500">{preview.evidence}</p>
                <p className="text-[11px] text-slate-500">{preview.reversible}</p>
              </div>
            )}
          </div>

          <DialogFooter>
            {preview ? (
              <Button data-testid={`${testid}-commit`} onClick={commit} disabled={busy}
                className="rounded-lg bg-[#B01E28] hover:bg-[#8F1820] text-white h-11">
                {busy ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : null}
                Yes — record {preview.to?.stock_number} as its source
              </Button>
            ) : (
              <Button data-testid={`${testid}-next`} onClick={ask} disabled={blocked}
                className="rounded-lg bg-[#E0781A] hover:bg-[#C76714] text-white h-11 disabled:opacity-50">
                {busy ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : null}
                {!target.trim() ? "Scan the truck first" : !reason ? "Say how you know" : "What happens if I do?"}
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

// WHAT THE PART'S OWN RECORD SAYS. His ruling: "show the reason on the part, not just in a log, so
// it's visible when it matters" — and a documented match must read differently from a memory.
// Carries the reversal, because the same screen that claims the truck is where somebody disputes it.
export function SourceTruckLine({ part, onDone, testid = "source-line" }) {
  const { has } = useAuth();
  const navigate = useNavigate();
  const goDesk = useGoDesk();
  const { pathname } = useLocation();
  const [state, setState] = useState(null);
  const [open, setOpen] = useState(false);
  const [preview, setPreview] = useState(null);
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);

  const id = part?.id;
  const load = useCallback(async () => {
    if (!id) return;
    try { setState((await api.get(`/parts/${id}/source-truck`)).data); } catch { /* never break the record */ }
  }, [id]);
  useEffect(() => { load(); }, [load]);

  if (!state?.assigned) return null;

  const go = () => (pathname.startsWith("/floor") ? goDesk : navigate)(`/donors/${state.donor?.id}`);

  const ask = async () => {
    setBusy(true);
    try { setPreview((await api.post(`/parts/${id}/source-truck/reverse`, { note, confirm: false })).data); }
    catch (err) { scanRefuse(formatApiErrorDetail(err.response?.data?.detail)); toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  const commit = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/parts/${id}/source-truck/reverse`, { note, confirm: true });
      scanAccept(data.message); toast.success(data.message, { duration: 9000 });
      setOpen(false); setPreview(null); setNote(""); await load(); onDone?.();
    } catch (err) { scanRefuse(formatApiErrorDetail(err.response?.data?.detail)); toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const documented = state.kind === "documented";
  return (
    <div data-testid={testid} className="rounded-lg border-2 border-slate-200 bg-white px-3 py-2 space-y-1">
      <p className="text-sm text-slate-700 flex flex-wrap items-center gap-x-1.5">
        <Truck className="w-4 h-4 text-[#E0781A]" />
        <span>Traced to</span>
        <button type="button" data-testid={`${testid}-truck`} onClick={go}
          className="font-mono font-bold text-[#0369a1] underline">{state.donor?.stock_number || "a truck"}</button>
        <span className={`rounded px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wide ${
          documented ? "bg-emerald-100 text-emerald-800" : "bg-slate-200 text-slate-600"}`}
          data-testid={`${testid}-kind`}>{documented ? "documented" : "judgement"}</span>
      </p>
      <p data-testid={`${testid}-how`} className="text-xs text-slate-500">
        {state.how}{state.told_by ? ` — ${state.told_by}` : ""}
        {state.note ? ` · “${state.note}”` : ""}
        {state.by ? ` · recorded by ${state.by}` : ""}{state.at ? ` · ${String(state.at).slice(0, 10)}` : ""}
      </p>
      {state.component && (
        <p data-testid={`${testid}-component`} className="text-xs text-slate-600">
          It is this truck's <b>{state.component.label}</b> — the truck's own row says where it is now.
        </p>
      )}
      {state.was_legacy && (
        <p data-testid={`${testid}-was-legacy`} className="text-xs text-slate-500">
          Was a legacy part{state.lot ? ` on lot ${state.lot}` : ""} — that stays in the history.
          {state.recovery_booked_as === "legacy" && " It had already left, so its recovery stays booked as legacy; this truck's recovery number does not change."}
        </p>
      )}
      {state.reversals > 0 && (
        <p data-testid={`${testid}-reversals`} className="text-xs text-amber-700 font-semibold">
          This part's source has been taken off and put back {state.reversals}× — it shows on your Health screen.
        </p>
      )}
      {has("assign_source_truck") && (state.reversible ? (
        <button type="button" data-testid={`${testid}-reverse-open`} onClick={() => { setPreview(null); setNote(""); setOpen(true); }}
          className="text-xs font-semibold text-[#B01E28] underline inline-flex items-center gap-1">
          <Undo2 className="w-3.5 h-3.5" />Wrong truck? Take it back off
        </button>
      ) : (
        <p data-testid={`${testid}-locked`} className="text-xs text-slate-400 inline-flex items-center gap-1">
          <Lock className="w-3 h-3" />Money has moved against this — the source can't be taken back off now
        </p>
      ))}

      <Dialog open={open} onOpenChange={(v) => { if (!v) { setOpen(false); setPreview(null); } }}>
        <DialogContent className="rounded-xl max-w-lg">
          <DialogHeader>
            <DialogTitle data-testid={`${testid}-reverse-title`}>Take this truck back off?</DialogTitle>
            <DialogDescription>
              It goes back to none — never straight to another truck. That's what keeps a part's
              source from quietly moving between trucks.
            </DialogDescription>
          </DialogHeader>
          <Textarea data-testid={`${testid}-reverse-note`} value={note} rows={2}
            onChange={(e) => { setNote(e.target.value); setPreview(null); }}
            placeholder="Why is it wrong? (optional, but it's what the history will say)"
            className="rounded-lg text-sm" />
          {preview && (
            <div data-testid={`${testid}-reverse-confirm`} className="rounded-lg border-2 border-slate-300 bg-slate-50 p-3 space-y-2">
              <p className="text-sm font-semibold text-slate-800">{preview.prompt}</p>
              {(preview.warnings || []).map((w, i) => (
                <p key={i} data-testid={`${testid}-reverse-warning-${i}`}
                  className={`text-xs font-semibold rounded-md border px-2 py-1.5 ${LEVEL[w.level] || LEVEL.normal}`}>{w.text}</p>
              ))}
              <p className="text-[11px] text-slate-500">{preview.reversible}</p>
            </div>
          )}
          <DialogFooter>
            {preview ? (
              <Button data-testid={`${testid}-reverse-commit`} onClick={commit} disabled={busy}
                className="rounded-lg bg-[#B01E28] hover:bg-[#8F1820] text-white h-11">
                {busy ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : null}Yes — back to no source
              </Button>
            ) : (
              <Button data-testid={`${testid}-reverse-next`} onClick={ask} disabled={busy}
                className="rounded-lg bg-[#E0781A] hover:bg-[#C76714] text-white h-11">
                {busy ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : null}What happens if I do?
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
